<template>
	<div class="bottom-card-payment">
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="active" @toggling-popup="v => $emit('toggling-popup', v)">
			<div class="bottom-card-payment-container" v-if="active">
				<ModalHeader :title="getString('cards')" no-new-line />
				<CardSlashedTapToPay v-if="!terminalsToSelect.length" />
				<div class="all-pos-container" v-if="terminalsToSelect.length">
					<div class="pos-container" v-for="(pos, index) in terminalsToSelect" :class="{fullWidth: terminalsToSelect.length <= 1, localTerminal: ['tap_phone', 'bluetooth', 'handoff'].includes(pos.terminal_id), external: pos.terminal_id === 'external', pax: pos.terminal_id && pos.terminal_id.startsWith('pax'), dojo: pos.terminal_id && pos.terminal_id.startsWith('dojo'), ttpoi: pos.terminal_id && pos.terminal_id === 'tap_phone' && isIOS}" :key="pos.terminal_id" @click="sendPayment(allTerminals.indexOf(pos))">
						<p>{{ pos.name }}</p>
					</div>
				</div>
				<div v-show="!!otherTerminalsToSelect?.length" class="other-pos-container">
					<div class="row other-pos-heading">
						<p>{{ getString('other_terminals') }}</p>
						<div class="toggle" @click="activeOtherPos = !activeOtherPos">
							<AppChevronIcon :direction="activeOtherPos ? 'up' : 'down'" />
						</div>
					</div>
					<AppDropdown white :collapsed="!activeOtherPos">
						<div class="all-pos-container">
							<div class="pos-container" v-for="(pos, index) in otherTerminalsToSelect" :class="{fullWidth: otherTerminalsToSelect.length <= 1, localTerminal: ['tap_phone', 'bluetooth', 'handoff'].includes(pos.terminal_id), external: pos.terminal_id === 'external', pax: pos.terminal_id && pos.terminal_id.startsWith('pax'), dojo: pos.terminal_id && pos.terminal_id.startsWith('dojo')}" :key="pos.terminal_id" @click="sendPayment(allTerminals.indexOf(pos))">
								<p>{{ pos.name }}</p>
							</div>
						</div>
					</AppDropdown>
				</div>
			</div>
		</AppBottomSheet>
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="tapPhonePairingActive" @toggling-popup="toggleTapPhonePairing">
			<LocalTerminalSheet v-show="tapPhonePairingActive" ref="localTerminalSheet" @done="onTapPhonePairingDone" />
		</AppBottomSheet>
		<AppPopup ref="popup-confirm-external" :title="getString('confirm_external_pos')" :body-text="getString('confirm_external_pos_explain', [currencyStore.readableCurrencyValue(amountToPay)])" :buttons="[{text: getString('cancel'), negative: true, callback: hidePopup}, {text: getString('confirm'), callback: completePayment}]" />
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import CardSlashedTapToPay from "@/console-new/components/shared/CardSlashedTapToPay.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useCounterStore, useUserStore, useNativeStore} from "@/console-new/stores";
import {useSunmiStore} from "@/console-sun/stores";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import {isIOS, isAndroid, isNative} from "@/shared/helpers/webapp";
import CardSlashed from "@/console-new/components/shared/CardSlashed.vue";
import AppButton from "@/shared/components/AppButton.vue";
import LocalTerminalSheet from "@/console-sun/components/LocalTerminalSheet.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let svgStore = useSvgStore();
		const counterStore = useCounterStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		const nativeStore = useNativeStore();
		const sunmiStore = useSunmiStore();
		const notificationStore = useNotificationStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore, currencyStore, languageStore, counterStore, nativeStore, sunmiStore, getIcon: svgStore.get};
	},
	name: "BottomCardPayment",
	components: {AppButton, CardSlashed, AppDropdown, AppChevronIcon, CardSlashedTapToPay, AppPopup, AppSelect, ModalHeader, AppBottomSheet, LocalTerminalSheet},
	mixins: [WindowWidth],
	emits: ["toggling-popup", "payment-done", "start-payment", "refresh-terminals"],
	data() {
		return {
			activeOtherPos: false,
			tapPhonePairingActive: false,
			pendingTerminalForPayment: null,
		}
	},
	props: {
		active: {
			type: Boolean,
			required: true,
		},
		terminals: {
			type: Array,
			required: true,
		},
		amountToPay: {
			type: Number,
			required: true,
		},
	},
	computed: {
		isIOS() {
			return isIOS();
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		allTerminals() {
			let freePlan = this.userStore.currBusiness?.active_plan === 'free';
			let terminalsArray = this.terminals.slice();
			
			// Add local terminal if device supports it
			if(isNative()) {
				const hasTapPhone = terminalsArray.some(t => t.terminal_id === 'tap_phone');
				if(!hasTapPhone && (isIOS() || (isAndroid() && Android.getManufacturer() !== "BBPOS"))) {
					terminalsArray.push({
						terminal_id: "tap_phone",
						name: isAndroid() ? "Tap Phone" : this.getString("tap_phone_ios"),
					});
				}
			}

			if(freePlan) {
				return terminalsArray;
			}
			return [{
				name: this.getString('external_pos'),
				terminal_id: "external",
			}].concat(terminalsArray);
		},
		otherTerminalsToSelect() {
			let excludedTerminalsIds = this.userStore?.userHiddenTerminalsIds;
			let returningTerminals = [];
			if(this.allTerminals?.[0] && this.allTerminals[0].terminal_id === 'external') {
				returningTerminals.push(this.allTerminals[0]);
			}
			if(!excludedTerminalsIds?.length) {
				return returningTerminals;
			}
			returningTerminals.push(...this.allTerminals.filter(terminal => excludedTerminalsIds.includes(terminal.terminal_id) && terminal.terminal_id !== 'external'));
			return returningTerminals;
		},
		terminalsToSelect() {
			let excludedTerminalsIds = this.userStore?.userHiddenTerminalsIds || [];
			return this.allTerminals.filter(terminal => !excludedTerminalsIds.includes(terminal.terminal_id) && terminal.terminal_id !== 'external');
		}
	},
	methods: {
		getSelectedTerminal(index) {
			if (index >= 0 && index < this.allTerminals.length) {
				return this.allTerminals[index];
			} else {
				return null;
			}
		},
		sendPayment(index) {
			let terminal = this.getSelectedTerminal(index);
			if(terminal && terminal.terminal_id !== 'external') {

				if(terminal.terminal_id === 'tap_phone' && localStorage.getItem("tapPhoneActive") !== "1") {

					if(isNative()) {
						if(
							isIOS() ||
							(isAndroid() && Android.checkPermissions && Android.checkPermissions())
						) {
							this.pendingTerminalForPayment = terminal;
							this.tapPhonePairingActive = true;
							// Pre-select the appropriate reader type after the component mounts
							this.$nextTick(() => {
								this.setInitialReaderType();
							});
						} else {
							this.showNotification('need-permissions');
						}
					}

				} else {
					localStorage.setItem("lastTerminalId", terminal.terminal_id);
					this.$emit("start-payment", terminal.terminal_id);
					this.$emit("toggling-popup", false);
				}
			} else {
				this.$refs['popup-confirm-external'].toggleStatus(true);
			}
		},
		hidePopup() {
			this.$refs['popup-confirm-external'].toggleStatus(false);
		},
		completePayment() {
			this.hidePopup();
			this.$emit('payment-done');
			this.$emit("toggling-popup", false);
		},
		GoToShop() {
			this.$router.push("/plugins/shop");
		},
		toggleTapPhonePairing(isOpen) {
			if(!isOpen) {
				this.tapPhonePairingActive = false;
				this.pendingTerminalForPayment = null;
			}
		},
		onTapPhonePairingDone() {
			let pendingTerminal = this.pendingTerminalForPayment;
			this.tapPhonePairingActive = false;
			this.$emit("refresh-terminals", pendingTerminal.terminal_id, ()=>{
				this.$nextTick(()=>{
					if(pendingTerminal) {
						let pendingTerminalIndex = this.allTerminals.findIndex(t => t.terminal_id === pendingTerminal.terminal_id);
						if(pendingTerminalIndex !== -1) {
							this.sendPayment(pendingTerminalIndex);
						}
					}
				})
			});
		},
		setInitialReaderType() {
			if(this.$refs.localTerminalSheet) {
				this.$refs.localTerminalSheet.readerType = 'tap_on_phone';
				this.$refs.localTerminalSheet.clickActivate();
			}
		},
	},
	watch: {
		active(v) {
			if(!v) {
				this.activeOtherPos = false;
				this.tapPhonePairingActive = false;
				this.pendingTerminalForPayment = null;
			}
		},
		"sunmiStore.successTapPhoneConnection": {
			handler(newValue, oldValue) {
				if(oldValue !== newValue && newValue && this.tapPhonePairingActive) {
					// Reset the success flag, used to trigger this action
					this.sunmiStore.successTapPhoneConnection = false;
					// Call the done handler to proceed with payment
					this.onTapPhonePairingDone();
				}
			}
		},
	}
}
</script>

<style scoped lang="scss">
.select-row {
	.select-wrapper {
		max-width: 200px;

		@media (max-width: 400px) {
			max-width: 100%;
		}
	}

	p {
		@media (max-width: 400px) {
			margin-bottom: 5px;
			width: 100%;
		}
	}
}

.all-pos-container {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	margin-bottom: 10px;

	@media (max-width: 400px) {
		flex-direction: column;
	}

	.pos-container {
		display: flex;
		align-items: center;
		justify-content: center;
		width: calc(50% - 5px);
		padding: 10px;
		border: 1px solid var(--color-primary);
		box-shadow: 0 2px 3px var(--color-box-shadow-1);
		background-color: var(--color-bg-primary-2);
		color: var(--color-primary);
		border-radius: 13px;
		cursor: pointer;
		transition: box-shadow 0.2s ease-in-out;
		height: 100px;
		@include noSelect();

		@media (max-width: 400px) {
			width: 100%;
		}

		&:hover {
			box-shadow: 0 2px 3px var(--color-bg-alpha-0);
		}

		&.fullWidth {
			width: 100%;
		}

		&.localTerminal {
			background-color: $secondary;
		}

		&.ttpoi {
			flex: 1;
			width: 100%;
			min-height: 100px;
		}

		&.external {
			background-color: var(--color-bg);
			border: 1px solid $alpha_4;
			box-shadow: none;
		}

		p {
			color: $primary;
			font-weight: 500;
		}
		&.pax {
			background-color: rgba(92, 55, 22, 0.7);
		}
		&.dojo {
			background-color: rgba(87, 125, 157, 0.7);
		}
	}
}
.other-pos-container {
	@include noSelect();
	margin-top: 20px;
	padding: 10px;
	border: 1px solid var(--color-primary);
	border-radius: 13px;

	.dropdown-container :deep(.dropdown .dropdown-content) {
		box-shadow: none;
		padding: 0;

		.all-pos-container {
			margin-bottom: 0;
		}
	}

	.other-pos-heading {
		align-items: center;
		justify-content: space-between;

		p {
			@include truncateString(1);
			max-width: calc(100% - 35px);
		}
		.toggle {
			background-color: var(--color-bg-primary-2);
			border-radius: 13px;
			transition: transform 0.2s $transition;
			cursor: pointer;
			padding: 10px;
			max-width: 35px;
			&:hover {
				transform: scale(1.1);
			}
		}
	}

	@media (max-width: 400px) {
		padding: 10px 5px;

		.other-pos-heading {
			p {
				max-width: calc(100% - 25px);
			}
			.toggle {
				max-width: 25px;
				padding: 5px;
				border-radius: 10px;
			}
		}
	}
}
</style>